import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { Button, InputLabel, Select, Table, TextInput, ValueListInput } from '@components/Common';
import DateTimePicker from '@components/Common/DateTimePicker/DateTimePicker';
import { formatLocalDate } from '@helpers/formatting';
import { addYearsToDate, toIsoUtc } from '@helpers/dateUtils';
import { Terminal } from '@api/queries/terminals';
import { LoadingTraining, TransportationCompanyDriver } from '@api/queries/transportationCompanies';

const InputRow = styled.div`
  display: flex;
  flex-direction: row;

  > *:nth-child(1) {
    margin-right: 10px;
  }

  > *:nth-child(3) {
    margin-left: 10px;
  }

  > * {
    width: 50%;
  }
`;

const ButtonRow = styled.div`
  margin-top: 15px;
  > * {
    margin-right: 10px;
  }
`;

const EditorContainer = styled.div`
  border: 1px solid #ccc;
  padding: 10px;
  margin-top: 3px;
  margin-bottom: 30px;
`;

const TitleRow = styled.div`
  display: flex;
  flex-direction: row;

  > *:nth-child(2) {
    padding: 10px 5px 0px;
    margin: 10px auto;
    text-align: right;
  }

  > * {
    width: 50%;
  }
`;

const defaultDriver: TransportationCompanyDriver = {
  id: null,
  name: '',
  rfids: [],
  loadingTrainings: []
};

const defaultLoadingTraining: LoadingTraining = {
  terminal: '',
  loadingPoint: '',
  validUntil: toIsoUtc(addYearsToDate(new Date(), 1))
};

interface Props {
  terminals: Terminal[];
  values: TransportationCompanyDriver[];
  onChange: (newValues: TransportationCompanyDriver[]) => void;
  editorItem: TransportationCompanyDriver | null;
  onSetEditorItem: (item: TransportationCompanyDriver | null) => void;
}

const Drivers: React.FC<Props> = ({ values, editorItem, onChange, onSetEditorItem, terminals }) => {
  const { t } = useTranslation();
  const columns = [
    {
      Header: t('transportationCompanies.name'),
      id: 'name',
      accessor: 'name',
      width: 180
    },
    // {
    //   Header: t('transportationCompanies.adr_valid_until'),
    //   id: 'adrCertificateValidUntil',
    //   accessor: 'adrCertificateValidUntil',
    //   Cell: ({ value }) => formatDateTimeLuxon(value, false),
    //   maxWidth: 150
    // },
    {
      Header: 'RFID',
      id: 'rfids',
      accessor: 'rfids',
      Cell: ({ value }) => value.join(', ')
    },
    {
      Header: t('transportationCompanies.loading_trainings'),
      id: 'loadingTrainings',
      accessor: 'loadingTrainings',
      Cell: ({ value }: { value: LoadingTraining[] }) => value.map(v => v.terminal).join(', ')
    }
  ];

  const handleEditorSave = (item: TransportationCompanyDriver) => {
    if (!item.id) {
      onChange([...values, { ...item, id: uuidv4() }]);
    } else {
      const index = values.findIndex(v => v.id === item.id);
      const newValues = [...values];
      newValues[index] = item;
      onChange(newValues);
    }

    onSetEditorItem(null);
  };

  const handleEditorRemove = (item: TransportationCompanyDriver) => {
    if (confirm(t('transportationCompanies.confirm_delete_driver'))) {
      const newValues = values.filter(v => v !== item);
      onSetEditorItem(null);
      onChange(newValues);
    }
  };

  const handleEditorCancel = () => {
    onSetEditorItem(null);
  };

  return (
    <div>
      <TitleRow>
        <InputLabel>{t('transportationCompanies.drivers')}</InputLabel>
        <div>
          <a onClick={e => onSetEditorItem({ ...defaultDriver })}>{t('transportationCompanies.add_driver')}</a>
        </div>
      </TitleRow>
      {values.length > 0 && (
        <Table
          data={values}
          columns={columns}
          filterable={false}
          showPagination={false}
          defaultSortColumn="name"
          defaultSortDesc={true}
          key={values.length}
          defaultPageSize={values.length}
          onRowSelected={({ original }) => onSetEditorItem(original)}
          rowSelector={i => editorItem && editorItem.id === i.original.id}
        />
      )}
      {!!editorItem && (
        <DriverEditor
          key={editorItem.id || 'new'}
          terminals={terminals}
          item={editorItem}
          onSave={handleEditorSave}
          onRemove={handleEditorRemove}
          onCancel={handleEditorCancel}
        />
      )}
    </div>
  );
};

interface DriverEditorProps {
  terminals: Terminal[];
  item: TransportationCompanyDriver;
  onSave: (item: TransportationCompanyDriver) => void;
  onRemove: (item: TransportationCompanyDriver) => void;
  onCancel: () => void;
}

const DriverEditor: React.FC<DriverEditorProps> = ({ item, onSave, onRemove, onCancel, terminals }) => {
  const { t } = useTranslation();
  const [loadingTrainingEditorItem, setLoadingTrainingEditorItem] = useState<LoadingTraining>(null);
  const [editItem, setEditItem] = useState({ ...item });
  const { name, rfids, loadingTrainings } = editItem;

  const handleTextInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditItem({
      ...editItem,
      [e.target.name]: e.target.value
    });
  };

  const isNew = !editItem.id;
  const isValid = name.trim().length > 0;
  const isSetDriverDisabled = !isValid || !!loadingTrainingEditorItem;

  const handleRfidsChanged = (newValues: any[]) => {
    setEditItem({
      ...editItem,
      rfids: newValues
    });
  };

  const handleDriverSet = () => {
    if (isValid) {
      onSave(editItem);
    }
  };

  const handleDriverRemove = () => {
    onRemove(item);
  };

  const handleLoadingTrainingSave = (newItem: LoadingTraining, originalItem?: LoadingTraining) => {
    const newValues = [...loadingTrainings];

    if (!originalItem) {
      newValues.push(newItem);
    } else {
      const index = loadingTrainings.findIndex(v => v === newItem);
      newValues[index] = newItem;
    }

    setLoadingTrainingEditorItem(null);
    setEditItem({
      ...editItem,
      loadingTrainings: newValues
    });
  };

  const handleLoadingTrainingRemove = (item: LoadingTraining) => {
    setEditItem({
      ...editItem,
      loadingTrainings: loadingTrainings.filter(l => l !== item)
    });
  };

  const handleLoadingTrainingCancel = () => {
    setLoadingTrainingEditorItem(null);
  };

  return (
    <EditorContainer>
      <InputRow>
        <div>
          <InputLabel htmlFor="name">{t('transportationCompanies.name')} *</InputLabel>
          <TextInput id="name" name="name" type="text" value={name} onChange={handleTextInputChanged} autoFocus />
        </div>
        <div>
          <InputLabel htmlFor="rfids">{t('transportationCompanies.rfids')}</InputLabel>
          <ValueListInput
            name="rfids"
            placeholder={t('transportationCompanies.rfids_placeholder')}
            values={rfids}
            onChange={handleRfidsChanged}
          />
        </div>
      </InputRow>
      <div>
        <TitleRow>
          <InputLabel>{t('transportationCompanies.loading_trainings')}</InputLabel>
          <div>
            <a onClick={e => setLoadingTrainingEditorItem({ ...defaultLoadingTraining })}>
              {t('transportationCompanies.add_loading_training')}
            </a>
          </div>
        </TitleRow>
        {loadingTrainings.length > 0 && (
          <Table
            data={loadingTrainings}
            columns={[
              {
                Header: t('entry.terminal'),
                id: 'terminal',
                accessor: 'terminal',
                width: 180
              },
              {
                Header: t('transportationCompanies.loading_point'),
                id: 'loadingPoint',
                accessor: 'loadingPoint'
              },
              {
                Header: t('transportationCompanies.valid_until'),
                id: 'validUntil',
                accessor: 'validUntil',
                Cell: ({ value }) => formatLocalDate(value),
                maxWidth: 150
              }
            ]}
            filterable={false}
            showPagination={false}
            defaultSortColumn="terminal"
            defaultSortDesc={true}
            key={loadingTrainings.length}
            defaultPageSize={loadingTrainings.length}
            onRowSelected={({ original }) => setLoadingTrainingEditorItem(original)}
            rowSelector={i => loadingTrainingEditorItem && loadingTrainingEditorItem === i.original}
          />
        )}
        {!!loadingTrainingEditorItem && (
          <LoadingTrainingEditor
            terminals={terminals}
            item={loadingTrainingEditorItem}
            onSave={handleLoadingTrainingSave}
            onRemove={handleLoadingTrainingRemove}
            onCancel={handleLoadingTrainingCancel}
          />
        )}
      </div>
      <ButtonRow>
        <Button type="button" secondary={true} onClick={onCancel} small={true}>
          {t('cancel')}
        </Button>
        {!isNew && (
          <Button type="button" danger={true} onClick={handleDriverRemove} small={true}>
            {t('delete')}
          </Button>
        )}
        <Button primary={true} disabled={isSetDriverDisabled} onClick={handleDriverSet} small={true}>
          {t('set')}
        </Button>
      </ButtonRow>
    </EditorContainer>
  );
};

interface LoadingTrainingEditorProps {
  terminals: Terminal[];
  item: LoadingTraining;
  onSave: (item: LoadingTraining, originalItem?: LoadingTraining) => void;
  onRemove: (item: LoadingTraining) => void;
  onCancel: () => void;
}

const LoadingTrainingEditor: React.FC<LoadingTrainingEditorProps> = ({
  item,
  onSave,
  onRemove,
  onCancel,
  terminals
}) => {
  const { t } = useTranslation();
  const [editItem, setEditItem] = useState<LoadingTraining>({ ...item });
  const { terminal, loadingPoint, validUntil } = editItem;

  const terminalOptions = terminals.map(i => ({ value: i.name, label: i.name }));
  const selectedTerminalOption = terminal ? { value: terminal, label: terminal } : null;

  const isNew = !item.terminal;
  const isValid = !!terminal && loadingPoint.trim().length > 0 && !!validUntil;

  const handleTextInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditItem({
      ...editItem,
      [e.target.name]: e.target.value
    });
  };

  const handleValidUntilChanged = (newValue: string) => {
    setEditItem({
      ...editItem,
      validUntil: newValue || null
    });
  };

  const handleTerminalChanged = (newValue, actionMeta) => {
    const value = actionMeta.action === 'clear' ? null : newValue.value;
    setEditItem({
      ...editItem,
      terminal: value
    });
  };

  const handleSetClick = () => {
    if (isValid) {
      onSave(editItem);
    }
  };

  const handleRemove = () => {
    onRemove(item);
  };

  return (
    <EditorContainer>
      <InputRow>
        <div>
          <InputLabel htmlFor="terminalId">{t('tank.terminal')} *</InputLabel>
          <Select
            name="terminalId"
            placeholder={t('select_terminal')}
            isClearable={false}
            value={selectedTerminalOption}
            onChange={handleTerminalChanged}
            options={terminalOptions}
            autoFocus
          />
        </div>
        <div>
          <InputLabel htmlFor="loadingPoint">{t('transportationCompanies.loading_point')} *</InputLabel>
          <TextInput
            id="loadingPoint"
            name="loadingPoint"
            type="text"
            value={loadingPoint}
            onChange={handleTextInputChanged}
          />
        </div>
        <div>
          <DateTimePicker
            title={t('transportationCompanies.valid_until') + ' *'}
            name="validUntil"
            dateTime={validUntil}
            hasTime={false}
            showTime={false}
            changed={handleValidUntilChanged}
          />
        </div>
      </InputRow>
      <ButtonRow>
        <Button type="button" secondary={true} onClick={onCancel} small={true}>
          {t('cancel')}
        </Button>
        {!isNew && (
          <Button type="button" danger={true} onClick={handleRemove} small={true}>
            {t('delete')}
          </Button>
        )}
        <Button primary={true} disabled={!isValid} onClick={handleSetClick} small={true}>
          {t('set')}
        </Button>
      </ButtonRow>
    </EditorContainer>
  );
};

export default Drivers;
